import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import clsx from "clsx";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import {
  applyReferenceDeletionPlan,
  buildNoteReferenceTokenText,
  getNoteContextAtCursor,
  planReferenceDeletion,
  prepareReferencesForSave,
  resolveReferencePositions,
  shiftReferencesAfter,
} from "@/projectOverview/notes/noteReferences";
import { applyMentionSeedToDraft, type MentionSeed } from "@/projectOverview/notes/replyMention";
import type { DraftNoteReference, NoteSuggestionItem } from "@/projectOverview/notes/types";
import { useNoteSuggestions } from "@/projectOverview/notes/useNoteSuggestions";

const MENTION_CHIPS = [
  { trigger: "@", label: "Mitarbeiter" },
  { trigger: "#", label: "Auftrag" },
  { trigger: "+", label: "Bestellung" },
  { trigger: "*", label: "Stundennachweis" },
] as const;

const MATERIAL_CHIP = { trigger: "%", label: "Material" } as const;
const DOCUMENT_CHIP = { trigger: "§", label: "Dokument" } as const;

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function highlightHtml(text: string, references: DraftNoteReference[]): string {
  const resolved = resolveReferencePositions(text, references);
  if (!resolved.length) return escapeHtml(text).replace(/\n/g, "<br>");
  const parts: string[] = [];
  let cursor = 0;
  for (const ref of resolved) {
    if (ref.start > cursor) {
      parts.push(escapeHtml(text.slice(cursor, ref.start)).replace(/\n/g, "<br>"));
    }
    parts.push(
      `<span class="eh-po-chat-ref eh-po-chat-ref--preview">${escapeHtml(text.slice(ref.start, ref.end))}</span>`,
    );
    cursor = ref.end;
  }
  if (cursor < text.length) {
    parts.push(escapeHtml(text.slice(cursor)).replace(/\n/g, "<br>"));
  }
  return parts.join("");
}

export function NoteComposer({
  orderId,
  isBuero,
  saving,
  error,
  onSubmit,
  enableOrderReferences = true,
  globalReferences = false,
  placeholder = "Nachricht eingeben …",
  initialText = "",
  initialReferences,
  mentionSeed = null,
  autoFocus = false,
  clearOnSubmit = true,
  embedded = false,
}: {
  orderId: number;
  isBuero: boolean;
  saving: boolean;
  error: string | null;
  onSubmit: (payload: {
    note: string;
    visibility: "all" | "buero";
    references: DraftNoteReference[];
  }) => void;
  /** False = nur @ und # (z. B. S-Ordner-Notizen ohne Auftragskontext). */
  enableOrderReferences?: boolean;
  /**
   * True = globale Suche ohne Auftragskontext (To-Dos): zusätzlich
   * + Bestellung, * Stundennachweis und § Dokument über alle Projekte hinweg.
   */
  globalReferences?: boolean;
  placeholder?: string;
  /** Startwerte für Bearbeitungsmodus (z. B. To-Do bearbeiten). */
  initialText?: string;
  initialReferences?: DraftNoteReference[];
  /** Antwort-Erwähnung, die an den Anfang des Feldes gesetzt wird. */
  mentionSeed?: MentionSeed | null;
  /** Cursor direkt ins Feld setzen (Antwort auf eine Erwähnung). */
  autoFocus?: boolean;
  /** False = Eingabe nach Senden behalten (z. B. Dialog mit eigener Validierung). */
  clearOnSubmit?: boolean;
  /** True = ohne Chat-Fußzeile, z. B. in der To-Do-Kachel. */
  embedded?: boolean;
}) {
  const [text, setText] = useState(initialText);
  const [references, setReferences] = useState<DraftNoteReference[]>(
    initialReferences ?? [],
  );
  const [visibility, setVisibility] = useState<"all" | "buero">("all");
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const highlightRef = useRef<HTMLDivElement | null>(null);
  const pendingCursorRef = useRef<number | null>(null);
  const textRef = useRef(text);
  const referencesRef = useRef(references);
  const appliedSeedRef = useRef<{ key: string; text: string } | null>(null);
  textRef.current = text;
  referencesRef.current = references;

  const suggestions = useNoteSuggestions(orderId, { globalSearch: globalReferences });

  const mentionChips: ReadonlyArray<{ trigger: string; label: string }> = globalReferences
    ? [...MENTION_CHIPS, MATERIAL_CHIP, DOCUMENT_CHIP]
    : enableOrderReferences
      ? MENTION_CHIPS
      : MENTION_CHIPS.filter((chip) => chip.trigger === "@" || chip.trigger === "#");
  const allowedTriggers = mentionChips.map((chip) => chip.trigger).join("");

  const syncHighlightScroll = useCallback(() => {
    const input = inputRef.current;
    const highlight = highlightRef.current;
    if (input && highlight) highlight.scrollTop = input.scrollTop;
  }, []);

  /**
   * Overlay und Textarea müssen identisch umbrechen, sonst steht der Caret
   * neben dem sichtbaren Text. Zeigt die Textarea eine vertikale Scrollbar,
   * ist ihr Inhalt schmaler als das Overlay (inset: 0) – deshalb die
   * Scrollbar-Breite rechts am Overlay abziehen.
   */
  const syncHighlightWidth = useCallback(() => {
    const input = inputRef.current;
    const highlight = highlightRef.current;
    if (!input || !highlight) return;
    const scrollbarWidth = Math.max(0, input.offsetWidth - input.clientWidth);
    const next = scrollbarWidth > 0 ? `${scrollbarWidth}px` : "0px";
    if (highlight.style.right !== next) highlight.style.right = next;
  }, []);

  const focusInputAt = useCallback(
    (cursor: number) => {
      pendingCursorRef.current = cursor;
      const input = inputRef.current;
      if (input) {
        input.focus();
        input.setSelectionRange(cursor, cursor);
        syncHighlightScroll();
      }
    },
    [syncHighlightScroll],
  );

  const placeReplyCursor = useCallback(() => {
    const input = inputRef.current;
    if (!input || input.disabled) return;
    const pending = pendingCursorRef.current;
    const already = document.activeElement === input;
    if (already && pending == null) return;
    input.focus();
    const cursor = Math.min(pending ?? input.value.length, input.value.length);
    input.setSelectionRange(cursor, cursor);
    if (!already) input.scrollIntoView({ block: "nearest" });
    syncHighlightScroll();
  }, [syncHighlightScroll]);

  useEffect(() => {
    if (mentionSeed) {
      const token = mentionSeed.references[0]?.token_text || "";
      const alreadyInText = Boolean(token) && textRef.current.includes(token);
      if (appliedSeedRef.current?.key !== mentionSeed.key || !alreadyInText) {
        const previousText = appliedSeedRef.current?.text ?? null;
        const result = applyMentionSeedToDraft(
          textRef.current,
          referencesRef.current,
          previousText,
          mentionSeed,
        );
        appliedSeedRef.current = { key: mentionSeed.key, text: mentionSeed.text };
        if (result) {
          textRef.current = result.text;
          referencesRef.current = result.references;
          setText(result.text);
          setReferences(result.references);
          pendingCursorRef.current = result.cursor;
        } else {
          pendingCursorRef.current = textRef.current.length;
        }
      }
    }

    if (!autoFocus && !mentionSeed) return;
    let stopped = false;
    const place = () => {
      if (!stopped) placeReplyCursor();
    };
    place();
    // Der Projekt-Dialog setzt den Fokus erst nach dem Öffnen. Mehrere
    // Versuche holen den Cursor danach ins Antwortfeld, auch wenn der
    // Name der Person noch nachgeladen wird.
    const timers = [40, 180, 450, 800].map((ms) => window.setTimeout(place, ms));
    return () => {
      stopped = true;
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, [mentionSeed, autoFocus, placeReplyCursor]);

  useLayoutEffect(() => {
    const pos = pendingCursorRef.current;
    if (pos == null) return;
    pendingCursorRef.current = null;
    const input = inputRef.current;
    if (!input) return;
    input.focus();
    input.setSelectionRange(pos, pos);
    syncHighlightScroll();
  });

  // Textarea wächst mit dem Inhalt (bis max-height aus dem CSS). Ohne Auto-Grow
  // erscheint schon ab der zweiten Zeile eine Scrollbar, die den Umbruch
  // gegenüber dem Overlay verschiebt.
  useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    input.style.height = "auto";
    input.style.height = `${input.scrollHeight}px`;
    syncHighlightWidth();
    syncHighlightScroll();
  }, [text, syncHighlightScroll, syncHighlightWidth]);

  useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      syncHighlightWidth();
      syncHighlightScroll();
    });
    observer.observe(input);
    return () => observer.disconnect();
  }, [syncHighlightScroll, syncHighlightWidth]);

  const refreshSuggestions = useCallback(
    (value: string, cursorPos?: number) => {
      const input = inputRef.current;
      const pos = cursorPos ?? input?.selectionStart ?? value.length;
      const ctx = getNoteContextAtCursor(value, pos);
      if (ctx && allowedTriggers.includes(ctx.trigger)) {
        void suggestions.load(ctx);
      } else {
        suggestions.close();
      }
    },
    [suggestions, allowedTriggers],
  );

  const applySuggestion = useCallback(
    (item: NoteSuggestionItem) => {
      const input = inputRef.current;
      const anchor = suggestions.anchor;
      if (!input || !anchor) return;

      const before = text.slice(0, anchor.start);
      const after = text.slice(anchor.end);
      const tokenText = buildNoteReferenceTokenText(anchor.trigger, item.label);
      const replacement = `${tokenText} `;
      const nextText = `${before}${replacement}${after}`;
      const cursor = before.length + replacement.length;
      const delta = replacement.length - (anchor.end - anchor.start);

      setText(nextText);
      setReferences((prev) => [
        ...shiftReferencesAfter(prev, anchor.end, delta),
        {
          type: item.refType,
          ref_id: item.id,
          label: item.label,
          trigger: anchor.trigger,
          token_text: tokenText,
          start: before.length,
          end: before.length + tokenText.length,
        },
      ]);
      suggestions.close();
      pendingCursorRef.current = cursor;
    },
    [suggestions, text],
  );

  function handleSubmit() {
    const noteText = text.trim();
    if (!noteText) return;
    const prepared = prepareReferencesForSave(noteText, references);
    onSubmit({
      note: noteText,
      visibility: isBuero && visibility === "buero" ? "buero" : "all",
      references: prepared,
    });
    if (clearOnSubmit) {
      setText("");
      setReferences([]);
    }
    suggestions.close();
  }

  return (
    <div className={clsx("eh-po-chat-composer", embedded && "eh-todo-composer")}>
      {isBuero ? (
        <div
          className="mb-2"
          onMouseDown={(e) => {
            if ((e.target as HTMLElement).closest("button")) e.preventDefault();
          }}
        >
          <SegmentedControl
            name="Notiz-Sichtbarkeit"
            value={visibility}
            onChange={(v) => {
              setVisibility(v);
              focusInputAt(text.length);
            }}
            disabled={saving}
            options={[
              { value: "all", label: "Alle" },
              { value: "buero", label: "Nur Büro" },
            ]}
          />
        </div>
      ) : null}

      <div
        className="mb-2 flex flex-wrap gap-1"
        aria-label="Erwähnungsmöglichkeiten"
        onMouseDown={(e) => {
          if ((e.target as HTMLElement).closest("button")) e.preventDefault();
        }}
      >
        {mentionChips.map((chip) => (
          <button
            key={chip.trigger}
            type="button"
            className="eh-po-chat-mention-chip"
            onClick={() => {
              const prefix = text && !/\s$/.test(text) ? `${text} ` : text;
              const next = `${prefix}${chip.trigger}`;
              const cursor = next.length;
              setText(next);
              refreshSuggestions(next, cursor);
              focusInputAt(cursor);
            }}
          >
            <strong>{chip.trigger}</strong> {chip.label}
          </button>
        ))}
      </div>

      <div className="eh-po-chat-input-row">
        <div className="eh-po-chat-input-wrap">
          <div
            ref={highlightRef}
            className={clsx(
              "eh-po-chat-input-highlight",
              !text && "eh-po-chat-input-highlight--empty",
            )}
            aria-hidden
            dangerouslySetInnerHTML={{
              __html: text
                ? highlightHtml(text, references)
                : escapeHtml(placeholder),
            }}
          />
          <textarea
            ref={inputRef}
            className="eh-po-chat-input"
            rows={1}
            placeholder={placeholder}
            value={text}
            disabled={saving}
            data-autofocus={autoFocus ? "" : undefined}
            spellCheck={false}
            onChange={(e) => {
              const v = e.target.value;
              const pos = e.target.selectionStart ?? v.length;
              setText(v);
              setReferences((prev) => resolveReferencePositions(v, prev));
              refreshSuggestions(v, pos);
            }}
            onClick={(e) =>
              refreshSuggestions(
                e.currentTarget.value,
                e.currentTarget.selectionStart ?? e.currentTarget.value.length,
              )
            }
            onKeyUp={(e) =>
              refreshSuggestions(
                e.currentTarget.value,
                e.currentTarget.selectionStart ?? e.currentTarget.value.length,
              )
            }
            onScroll={syncHighlightScroll}
            onBlur={() => window.setTimeout(() => suggestions.close(), 150)}
            onKeyDown={(e) => {
              if (e.key === "Backspace" || e.key === "Delete") {
                const input = e.currentTarget;
                const selStart = input.selectionStart ?? 0;
                const selEnd = input.selectionEnd ?? selStart;
                const plan = planReferenceDeletion(
                  text,
                  references,
                  selStart,
                  selEnd,
                  e.key,
                );
                if (plan) {
                  e.preventDefault();
                  const next = applyReferenceDeletionPlan(text, references, plan);
                  setText(next.noteText);
                  setReferences(next.references);
                  pendingCursorRef.current = plan.cursor;
                  refreshSuggestions(next.noteText, plan.cursor);
                  return;
                }
              }
              if (suggestions.open && suggestions.items.length > 0) {
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  suggestions.setActiveIndex(
                    (suggestions.activeIndex + 1) % suggestions.items.length,
                  );
                  return;
                }
                if (e.key === "ArrowUp") {
                  e.preventDefault();
                  suggestions.setActiveIndex(
                    (suggestions.activeIndex - 1 + suggestions.items.length) %
                      suggestions.items.length,
                  );
                  return;
                }
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  const picked = suggestions.items[suggestions.activeIndex];
                  if (picked) applySuggestion(picked);
                  return;
                }
                if (e.key === "Escape") {
                  suggestions.close();
                  return;
                }
              }
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
          />
          {suggestions.open ? (
            <div className="eh-po-chat-suggestions" role="listbox">
              {suggestions.loading ? (
                <p className="eh-po-chat-suggestion-hint">Suche …</p>
              ) : suggestions.items.length === 0 ? (
                <p className="eh-po-chat-suggestion-hint">Keine Treffer</p>
              ) : (
                suggestions.items.map((item, idx) => (
                  <button
                    key={`${item.refType}-${item.id}`}
                    type="button"
                    role="option"
                    aria-selected={idx === suggestions.activeIndex}
                    className={clsx(
                      "eh-po-chat-suggestion-item",
                      idx === suggestions.activeIndex && "is-active",
                    )}
                    onMouseDown={(ev) => {
                      ev.preventDefault();
                      applySuggestion(item);
                    }}
                  >
                    <span className="eh-po-chat-suggestion-label">{item.label}</span>
                    {item.subtitle ? (
                      <span className="eh-po-chat-suggestion-subtitle">{item.subtitle}</span>
                    ) : null}
                  </button>
                ))
              )}
            </div>
          ) : null}
        </div>
        <button
          type="button"
          className="eh-po-chat-send"
          disabled={saving || !text.trim()}
          onClick={handleSubmit}
          aria-label="Senden"
        >
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden>
            <path
              fill="currentColor"
              d="M2.01 21 23 12 2.01 3 2 10l15 2-15 2z"
            />
          </svg>
        </button>
      </div>

      {error ? <p className="mt-2 text-xs text-status-red">{error}</p> : null}
    </div>
  );
}
